import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useOrder } from '../../contexts/OrderContext';
import { useLocation } from '../../contexts/LocationContext';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiDollarSign, FiShoppingBag, FiCalendar, FiFilter, FiDownload } = FiIcons;

const OrderAnalytics = () => {
  const { orders } = useOrder();
  const { selectedLocation, locations } = useLocation();
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [locationFilter, setLocationFilter] = useState(selectedLocation?.id || 'all');

  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        break;
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        weekStart.setHours(0, 0, 0, 0);
        startDate = weekStart;
        endDate = new Date(weekStart);
        endDate.setDate(weekStart.getDate() + 7);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(0);
        endDate = customEndDate ? new Date(customEndDate) : new Date();
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        startDate = new Date(0);
        endDate = new Date();
    }

    return { startDate, endDate };
  };

  const filteredOrders = useMemo(() => {
    const { startDate, endDate } = getDateRange();
    
    return orders.filter(order => {
      const orderDate = new Date(order.createdAt);
      const dateMatch = orderDate >= startDate && orderDate < endDate;
      const locationMatch = locationFilter === 'all' || order.locationId === parseInt(locationFilter);
      return dateMatch && locationMatch;
    });
  }, [orders, dateRange, customStartDate, customEndDate, locationFilter]);

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.total, 0);
    const totalOrders = filteredOrders.length;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Most popular items
    const itemCounts = {};
    const itemRevenue = {};
    
    filteredOrders.forEach(order => {
      order.items.forEach(item => {
        const key = item.name;
        itemCounts[key] = (itemCounts[key] || 0) + item.quantity;
        itemRevenue[key] = (itemRevenue[key] || 0) + item.totalPrice;
      });
    });

    const popularItems = Object.entries(itemCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([name, count]) => ({
        name,
        count,
        revenue: itemRevenue[name] || 0
      }));

    // Daily revenue chart data
    const dailyRevenue = {};
    const { startDate, endDate } = getDateRange();
    
    // Initialize all dates in range with 0
    for (let d = new Date(startDate); d < endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      dailyRevenue[dateKey] = 0;
    }

    // Fill with actual data
    filteredOrders.forEach(order => {
      const dateKey = new Date(order.createdAt).toISOString().split('T')[0];
      dailyRevenue[dateKey] = (dailyRevenue[dateKey] || 0) + order.total;
    });

    const chartData = Object.entries(dailyRevenue)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, revenue]) => ({
        date: new Date(date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }),
        revenue
      }));

    // Order status distribution
    const statusCounts = filteredOrders.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {});

    return {
      totalRevenue,
      totalOrders,
      averageOrderValue,
      popularItems,
      chartData,
      statusCounts
    };
  }, [filteredOrders]);

  // Chart options
  const revenueChartOption = {
    title: {
      text: 'Revenue Over Time',
      textStyle: { color: '#8B5E3C' }
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const data = params[0];
        return `${data.axisValue}<br/>Revenue: $${data.value.toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: analytics.chartData.map(d => d.date),
      axisLabel: { color: '#8B5E3C' }
    },
    yAxis: {
      type: 'value',
      axisLabel: { 
        color: '#8B5E3C',
        formatter: '${value}'
      }
    },
    series: [{
      data: analytics.chartData.map(d => d.revenue),
      type: 'line',
      smooth: true,
      itemStyle: { color: '#8B5E3C' },
      areaStyle: { color: 'rgba(139, 94, 60, 0.1)' }
    }],
    grid: {
      left: '10%',
      right: '10%',
      bottom: '15%',
      top: '15%'
    }
  };

  const popularItemsChartOption = {
    title: {
      text: 'Most Popular Items',
      textStyle: { color: '#8B5E3C' }
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const data = params[0];
        return `${data.axisValue}<br/>Orders: ${data.value}<br/>Revenue: $${analytics.popularItems.find(item => item.name === data.axisValue)?.revenue.toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: analytics.popularItems.map(item => item.name),
      axisLabel: { 
        color: '#8B5E3C',
        rotate: 45,
        interval: 0
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#8B5E3C' }
    },
    series: [{
      data: analytics.popularItems.map(item => item.count),
      type: 'bar',
      itemStyle: { color: '#C7A76C' }
    }],
    grid: {
      left: '10%',
      right: '10%',
      bottom: '25%',
      top: '15%'
    }
  };

  const exportData = () => {
    const csvContent = [
      ['Order ID', 'Date', 'Customer', 'Items', 'Total', 'Status'].join(','),
      ...filteredOrders.map(order => [
        order.orderNumber,
        new Date(order.createdAt).toLocaleString(),
        order.customerInfo.name,
        order.items.length,
        order.total.toFixed(2),
        order.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Order Analytics</h2>
          <p className="text-warm-brown/70">Track performance and insights</p>
        </div>
        <button
          onClick={exportData}
          className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
        >
          <SafeIcon icon={FiDownload} className="text-sm" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <SafeIcon icon={FiFilter} className="text-warm-brown" />
          <span className="text-warm-brown font-medium">Filters</span>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-warm-brown font-medium mb-2">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div>
            <label className="block text-warm-brown font-medium mb-2">Location</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
            >
              <option value="all">All Locations</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {dateRange === 'custom' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-warm-brown font-medium mb-2">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>
              <div>
                <label className="block text-warm-brown font-medium mb-2">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-brown/70 text-sm font-medium">Total Revenue</p>
              <p className="text-2xl font-bold text-warm-brown mt-1">
                ${analytics.totalRevenue.toFixed(2)}
              </p>
            </div>
            <div className="bg-green-500 w-12 h-12 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="text-white text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-brown/70 text-sm font-medium">Total Orders</p>
              <p className="text-2xl font-bold text-warm-brown mt-1">
                {analytics.totalOrders}
              </p>
            </div>
            <div className="bg-blue-500 w-12 h-12 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiShoppingBag} className="text-white text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-brown/70 text-sm font-medium">Average Order</p>
              <p className="text-2xl font-bold text-warm-brown mt-1">
                ${analytics.averageOrderValue.toFixed(2)}
              </p>
            </div>
            <div className="bg-purple-500 w-12 h-12 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-white text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-brown/70 text-sm font-medium">Date Range</p>
              <p className="text-lg font-medium text-warm-brown mt-1 capitalize">
                {dateRange === 'custom' ? 'Custom' : dateRange}
              </p>
            </div>
            <div className="bg-orange-500 w-12 h-12 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCalendar} className="text-white text-xl" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <ReactECharts option={revenueChartOption} style={{ height: '400px' }} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <ReactECharts option={popularItemsChartOption} style={{ height: '400px' }} />
        </motion.div>
      </div>

      {/* Popular Items Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-warm-brown/10">
          <h3 className="text-lg font-playfair text-warm-brown">Top Selling Items</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream">
              <tr>
                <th className="px-6 py-3 text-left text-warm-brown font-medium">Item</th>
                <th className="px-6 py-3 text-left text-warm-brown font-medium">Orders</th>
                <th className="px-6 py-3 text-left text-warm-brown font-medium">Revenue</th>
                <th className="px-6 py-3 text-left text-warm-brown font-medium">Avg Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-brown/10">
              {analytics.popularItems.map((item, index) => (
                <tr key={item.name} className="hover:bg-cream/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <span className="bg-warm-brown text-cream w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <span className="font-medium text-warm-brown">{item.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-warm-brown">{item.count}</td>
                  <td className="px-6 py-4 text-warm-brown font-medium">
                    ${item.revenue.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-warm-brown">
                    ${(item.revenue / item.count).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default OrderAnalytics;